/**
 * FastAPI отдаёт detail строкой, но на 422 — массивом объектов { loc, msg, type }.
 * Такой detail нельзя положить в state и отрендерить: React падает с
 * "Objects are not valid as a React child".
 */
export const extractErrorMessage = (error, fallback = 'Ошибка загрузки данных') => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string' && detail) return detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (typeof item === 'string' ? item : item?.msg))
      .filter(Boolean);
    if (messages.length) return messages.join('; ');
  }

  if (typeof detail?.msg === 'string') return detail.msg;

  return fallback;
};

/**
 * ТЗ раздел 10: 403 всегда показывается как «Недостаточно прав», а не как
 * технический текст бэкенда. Остальные статусы обрабатываются как обычно.
 * 401 сюда не попадает: его перехватывает интерцептор src/services/api.js.
 */
export const describeHttpError = (error, fallback = 'Ошибка загрузки данных') => {
  if (error?.response?.status === 403) return 'Недостаточно прав';
  return extractErrorMessage(error, fallback);
};

/**
 * Текст ошибки для пользователя: русский detail backend показывается как есть,
 * а английский, технический (snake_case-коды) или массив валидации 422
 * заменяется на понятный fallback (STYLE_GUIDE: без технических слов).
 */
export const describeUserError = (error, fallback = 'Ошибка загрузки данных') => {
  if (error?.response?.status === 403) return 'Недостаточно прав';
  const detail = error?.response?.data?.detail;
  if (typeof detail !== 'string' || !detail.trim()) return fallback;
  if (!/[А-Яа-яЁё]/.test(detail) || /[A-Za-z0-9]+_[A-Za-z0-9_]+/.test(detail)) return fallback;
  return detail;
};

export const FEATURE_NOT_ACTIVATED_MESSAGE = 'Функция пока не активирована';

const FEATURE_OFF_PATTERN = /feature|flag|disabled|not[\s_-]*enabled|not[\s_-]*activated|не\s+активирован|отключен/i;

/**
 * ТЗ V5 №12/№13: запись выключена флагом на backend. Контракт ответа при
 * выключенном флаге не зафиксирован, поэтому признаки — 501, маршрут не
 * подключён (404 с общим «Not Found») или detail/code про выключенную функцию
 * при 403/404/409/503. Обычный 403 по правам сюда не попадает.
 */
export const isFeatureNotActivatedError = (error) => {
  const status = error?.response?.status;
  if (!status) return false;
  if (status === 501) return true;
  const data = error.response.data;
  const detail = typeof data?.detail === 'string' ? data.detail : '';
  const code = typeof data?.code === 'string' ? data.code : (typeof data?.error === 'string' ? data.error : '');
  if (status === 404 && detail.trim().toLowerCase() === 'not found') return true;
  if (![403, 404, 409, 423, 503].includes(status)) return false;
  return FEATURE_OFF_PATTERN.test(`${detail} ${code}`);
};

/** describeUserError, но выключенная флагом функция называется прямо. */
export const describeWriteError = (error, fallback) => (
  isFeatureNotActivatedError(error) ? FEATURE_NOT_ACTIVATED_MESSAGE : describeUserError(error, fallback)
);
